import React, { useEffect, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import Input from '../../../../components/Form/Input'
import Button from '../../../../components/Form/Button'
import Select from '../../../../components/Form/Select'
import Modal from '../../../../components/Modal'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import SecureDataTransfer from './SecureDataTransfer'
import { loadVGSCollect } from '@vgs/collect-js'
import './css/collect-vgs.css'
import { impersonateForOneTimePaymentApi, makeOneTimePaymentApi } from '../../../../apis/apis'
import PaymentAmountBalanceWarning from './PaymentAmountBalanceWarning'
const { VITE_VGS_VAULT_ID: VGS_VAULT_ID, VITE_VGS_VAULT_ENVIRONMENT: VGS_VAULT_ENVIRONMENT, VITE_VGS_COLLECT_VERSION: VGS_COLLECT_VERSION } = import.meta.env

interface MakeCreditCardPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  userOptions: Record<string, string>
  defaultUserId: string
  invoiceId: string
  balanceTotal?: number
  balanceDue?: number
}

const MakeCreditCardPaymentModal: React.FC<MakeCreditCardPaymentModalProps> = ({
  isOpen,
  onClose,
  userOptions,
  defaultUserId,
  invoiceId,
  balanceTotal = 0,
  balanceDue = 0,
}) => {
  const dispatch = useAppDispatch()
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)

  const [vgsValidations, setVgsValidations]: any = useState(null)
  const [validations, setValidations]: any = useState(null)
  const [form, setForm] = useState()
  const [isFormInitialized, setIsFormInitialized] = useState(false)

  const requireStr = 'This field is required'

  // Add default date calculation
  const d = new Date()
  const defaultDate = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`

  // Used to determine if we should use dark mode for the VGS iframe
  const isDarkMode = document.documentElement.classList.contains('dark')

  const validate = () => {
    if (vgsValidations === null) {
      return
    }
    const errors: any = {}
    const getValidationState = (name: any) => {
      const validationState: any = vgsValidations && Object.values(vgsValidations).filter((item: any) => item.name === name)[0]
      return {
        hasError: !validationState?.isValid,
        message: `This field ${validationState?.errorMessages[0]}`,
      }
    }
    ;['cardNumberToken', 'expirationDate', 'cvvToken'].forEach((name: any) => {
      const validationState = getValidationState(name)
      validationState.hasError && (errors[name] = validationState.message)
    })
    return errors
  }

  useEffect(() => {
    if (isOpen) {
      form && (form as any).reset()
    }
  }, [form, isOpen])

  useEffect(() => {
    // Reset form initialization state when modal closes
    if (!isOpen) {
      setIsFormInitialized(false)
      setVgsValidations(null)
      setForm(undefined)
      return
    }

    if (isFormInitialized === true) {
      return
    }

    setIsFormInitialized(true)
    setVgsValidations(null)
    const loadForm = async () => {
      const vgsCollect = await loadVGSCollect({
        vaultId: VGS_VAULT_ID as string,
        environment: VGS_VAULT_ENVIRONMENT as string,
        version: VGS_COLLECT_VERSION as string,
      }).catch((e) => {
        console.error(e)
      })
      const form = (vgsCollect as any).init((state: any) => setVgsValidations(state))
      const css = {
        'font-size': '14px',
        color: isDarkMode ? '#f3f4f6' : '#40545F',
        'font-family': '"Arial", sans-serif',
        background: isDarkMode ? '#1e293b' : '#ffffff',
        '&::placeholder': {
          color: isDarkMode ? '#6b7280' : '#C8D0DB',
        },
        'line-height': '21px',
        'font-weight': '400',
      }
      form.field('#card-number', {
        name: 'cardNumberToken',
        type: 'card-number',
        placeholder: '0000 0000 0000 0000',
        validations: ['required', 'validCardNumber'],
        showCardIcon: 'true',
        autoComplete: 'cc-number',
        css,
      })
      form.field('#card-expiration-date', {
        name: 'expirationDate',
        type: 'card-expiration-date',
        placeholder: 'MM / YY',
        validations: ['required', 'validCardExpirationDate'],
        autoComplete: 'cc-exp',
        css,
        serializers: [{ name: 'replace', options: { old: ' ', new: '', count: 2 } }],
      })
      form.field('#card-security-code', {
        name: 'cvvToken',
        type: 'card-security-code',
        placeholder: 'CVV',
        validations: ['required', 'validCardSecurityCode'],
        autoComplete: 'cc-csc',
        css,
      })
      setForm(form)
    }
    loadForm()
  }, [isOpen])

  useEffect(() => {
    setValidations(validate())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vgsValidations])

  if (!isOpen) return null

  const hasVgsError = (name: any, touched: any) => (touched?.[name] || vgsValidations?.[name]?.isTouched) && validations?.[name]

  return (
    <>
      {confirmModal}
      <CustomContentModal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-2xl">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Post One Time Credit Card Payment</h3>
          </div>
          <div className="px-6 py-4">
            <Formik
              initialValues={{
                userId: defaultUserId,
                firstName: '',
                lastName: '',
                cardNumberToken: '',
                expirationDate: '',
                cvvToken: '',
                vaultValues: {},
                memo: '',
                paymentDate: defaultDate,
                amount: '',
                isPayModePayment: false,
              }}
              validationSchema={Yup.object().shape({
                userId: Yup.string().required(requireStr),
                firstName: Yup.string()
                  .required(requireStr)
                  .matches(/^[a-zA-Z0-9]+$/, 'Must only contain letters and numbers'),
                lastName: Yup.string()
                  .required(requireStr)
                  .matches(/^[a-zA-Z0-9]+$/, 'Must only contain letters and numbers'),
                paymentDate: Yup.string()
                  .required(requireStr)
                  .matches(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/, 'M/D/YYYY format required'),
                amount: Yup.string()
                  .required(requireStr)
                  .test('num', 'Must be a number', (value) => !isNaN(Number(value)))
                  .test('min', 'Must be greater than zero', (value) => Number(value) > 0)
                  .matches(/^[0-9]+(.[0-9]{2})?$/, 'Please enter whole dollars and cents (e.g. 100.00)'),
                isPayModePayment: Yup.boolean().required(requireStr),
              })}
              validate={validate}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  // Show confirmation modal first
                  await new Promise((resolve, reject) => {
                    setConfirmModal(<Modal resolve={resolve} reject={reject} />)
                  })
                  setConfirmModal(false)

                  try {
                    setSubmitting(true)
                    const { impersonationToken } = await impersonateForOneTimePaymentApi(values.userId)

                    const payload = {
                      firstName: values.firstName,
                      lastName: values.lastName,
                      primary: false,
                      sendEmailNotificationUponCreation: false,
                      isPayModePayment: values.isPayModePayment,
                      ...(values.isPayModePayment ? { amount: Number(values.amount) } : {}),
                    }

                    // Call the billing portal payment method directly with our special impersonation token as a header
                    // because it already has a VGS inbound route set up
                    await(form as any).submit(
                      '/api/v1/billing/paymentMethod',
                      {
                        data: payload,
                        headers: {
                          Authorization: `Bearer ${impersonationToken}`,
                        },
                      },
                      async (_status: number) => {
                        if (_status !== 204) {
                          dispatch(
                            pushToast({
                              message: 'Error adding one-time payment method',
                              type: 'error',
                            })
                          )
                        } else {
                          dispatch(
                            pushToast({
                              message: 'Payment method successfully added',
                              type: 'success',
                            })
                          )
                          // Close modal early to prevent downstream errors from the endpoints
                          onClose()
                          setSubmitting(false)
                          // Make payment
                          try {
                            const [month, day, year] = values.paymentDate.split('/')
                            const paymentDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).getTime()
                            const makePaymentPayload = {
                              amount: Number(values.amount),
                              paymentDate: paymentDate,
                              memo: values.memo,
                              invoiceId: invoiceId,
                              userId: values.userId,
                            }

                            const makePaymentResponse = await makeOneTimePaymentApi(makePaymentPayload)

                            if (makePaymentResponse.status === 'FAILED') {
                              throw new Error(makePaymentResponse.errorMessage)
                            }

                            dispatch(
                              pushToast({
                                message: 'Payment posted',
                                type: 'success',
                              })
                            )
                          } catch (e: any) {
                            // Handle errors from makeOneTimePaymentApi
                            const errorMessage = e.response?.data?.message || e.message || 'Unknown error'

                            dispatch(
                              pushToast({
                                message: `Failed to charge one time payment method: ${errorMessage}`,
                                type: 'error',
                              })
                            )
                            setSubmitting(false)
                            onClose()
                          }
                        }
                      }
                    )
                  } catch (e: any) {
                    dispatch(
                      pushToast({
                        message: `Error adding one-time payment method: ${e.message}`,
                        type: 'error',
                      })
                    )
                  }
                } catch (e) {
                  // User canceled in the confirmation modal
                  setConfirmModal(false)
                }
              }}
            >
              {({ errors, touched, isSubmitting, isValidating, isValid, submitCount, values }) => (
                <Form>
                  <div className="grid grid-cols-1 gap-4 dark:text-gray-200">
                    <div>
                      <Field
                        component={Select}
                        name="userId"
                        label="On behalf of"
                        options={userOptions}
                        error={errors.userId && touched.userId ? errors.userId : undefined}
                        required
                      />
                    </div>

                    <div>
                      <Field
                        as={Input}
                        name="firstName"
                        label="First name"
                        required
                        error={errors.firstName && touched.firstName ? errors.firstName : undefined}
                      />
                    </div>

                    <div>
                      <Field as={Input} name="lastName" label="Last name" required error={errors.lastName && touched.lastName ? errors.lastName : undefined} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-left" htmlFor="card-number">
                        Credit Card Number
                      </label>
                      <div id="card-number" className="w-full p-2 border border-gray-300 rounded-md field-wrapper vgs-iframe" />
                      <div className="text-red-500 text-sm mt-1">{hasVgsError('cardNumberToken', touched)}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-left" htmlFor="card-expiration-date">
                          Expiration Date
                        </label>
                        <div id="card-expiration-date" className="w-full p-2 border border-gray-300 rounded-md field-wrapper vgs-iframe" />
                        <div className="text-red-500 text-sm mt-1">{hasVgsError('expirationDate', touched)}</div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-left" htmlFor="card-security-code">
                          CVV
                        </label>
                        <div id="card-security-code" className="w-full p-2 border border-gray-300 rounded-md field-wrapper vgs-iframe" />
                        <div className="text-red-500 text-sm mt-1">{hasVgsError('cvvToken', touched)}</div>
                      </div>
                    </div>

                    <div>
                      <Field
                        label="Amount"
                        id="amount"
                        name="amount"
                        as={Input}
                        required
                        error={errors.amount && touched.amount ? errors.amount : undefined}
                        helpText="Please enter whole dollars and cents (e.g. 100.00)"
                      />
                      <PaymentAmountBalanceWarning amount={values.amount} balanceTotal={balanceTotal} balanceDue={balanceDue} />
                    </div>

                    <div>
                      <Field
                        label="Payment date"
                        id="paymentDate"
                        name="paymentDate"
                        as={Input}
                        required
                        error={errors.paymentDate && touched.paymentDate ? errors.paymentDate : undefined}
                      />
                    </div>

                    <div className="flex items-center">
                      <label htmlFor="isPayModePayment" className="mr-2 block text-sm font-medium">
                        Paymode Payment?
                      </label>
                      <Field
                        type="checkbox"
                        id="isPayModePayment"
                        name="isPayModePayment"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                    </div>

                    <div>
                      <Field as={Input} name="memo" label="Memo (optional)" error={errors.memo && touched.memo ? errors.memo : undefined} />
                    </div>

                    {validations && Object.keys(validations).length > 0 && submitCount > 0 && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                        This form contains one or more errors. Please make corrections and try again.
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <SecureDataTransfer />
                    <div className="space-x-2">
                      <Button type="submit" disabled={isSubmitting || (isValidating && !isValid)}>
                        Post Payment
                      </Button>
                      <Button type="button" onClick={onClose} color="slate">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </CustomContentModal>
    </>
  )
}

export default MakeCreditCardPaymentModal
